import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
// import { GlassCard } from '../ui/GlassCard';
import { TiltCard } from '../ui/TiltCard';
import { KineticText } from '../ui/KineticText';

type Project = {
    title: string;
    category: string;
    description: string;
    image: string;
    className: string;
    tags: string[];
    liveUrl?: string;
    portfolioUrl?: string;
    contactUrl?: string;
};

const projects: Project[] = [
    {
        title: "FitSenseAI",
        category: "Fitness / AI Computer Vision",
        description: "AI gym coach with live form correction, spoken rep cues, and an Indian diet tracker that works from a browser camera.",
        image: "https://img.youtube.com/vi/HUX_dWDhBRk/hqdefault.jpg",
        className: "bento-large",
        tags: ["Fitness", "Mediapipe", "AI Coaching"],
        liveUrl: "https://fitsenseai.aidhunik.com/",
        portfolioUrl: "https://portfolio.lehana.in/#fitsenseai",
        contactUrl: "https://www.aidhunik.com/contact?project=FitSenseAI&service=FitSenseAI&subject=FitSenseAI%20project%20enquiry&message=I%20want%20to%20discuss%20FitSenseAI."
    },
    {
        title: "Lehana Platform",
        category: "Full Stack / DevOps",
        description: "Hybrid VM+VPS Architecture with Traefik routing and Docker auto-discovery.",
        image: "/project-lehana-hybrid-vm-autonomous-infrastructure-management.png",
        className: "bento-large",
        tags: ["Docker", "Traefik", "Python", "React"]
    },
    {
        title: "Meeting Master",
        category: "AI Productivity",
        description: "Automated meeting summaries and task extraction.",
        image: "/project-meeting-master-ai-realtime-semantic-transcription-and-analysis.png",
        className: "bento-tall",
        tags: ["AI", "NLP", "Next.js"]
    },
    {
        title: "EcoScraper",
        category: "Data Engineering",
        description: "Distributed web scraping system for environmental data.",
        image: "/project-ecoscraper-distributed-high-performance-data-extraction.png",
        className: "bento-wide",
        tags: ["Python", "Scrapy", "Redis"]
    },
    {
        title: "Health Monitor",
        category: "DevOps",
        description: "Real-time infrastructure health tracking dashboard.",
        image: "/project-health-monitor-agentic-observability-control-center.png",
        className: "",
        tags: ["Grafana", "Prometheus"]
    },
    {
        title: "Portfolio v1",
        category: "Frontend",
        description: "Previous portfolio iteration.",
        image: "/project-portfolio-v1-legacy-cinematic-3d-experience.png",
        className: "",
        tags: ["React", "Tailwind"]
    },
    {
        title: "Neural DNS",
        category: "Blockchain",
        description: "Decentralized domain name system.",
        image: "/project-neural-dns-blockchain-secure-decentralized-identity.png",
        className: "bento-wide",
        tags: ["Solidity", "Web3"]
    }
];

export function Projects() {
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    const navigateToProject = (url: string) => {
        window.location.href = url;
    };

    useTransform(scrollYProgress, [0, 1], [100, -100]);

    return (
        <section id="projects" ref={containerRef} className="py-24 px-6 md:px-12 relative">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mb-16 text-center"
                >
                    <h2 className="text-4xl md:text-6xl font-bold mb-4 flex justify-center gap-3 flex-wrap">
                        <KineticText
                            text="SELECTED"
                            className="text-emerald-500"
                            delay={0.2}
                        />
                        <KineticText
                            text="WORKS"
                            className="text-white"
                            delay={0.6}
                        />
                    </h2>
                    <p className="text-gray-400 max-w-2xl mx-auto">
                        A curation of my best engineering and design projects.
                    </p>
                </motion.div>

                <div className="bento-grid">
                    {projects.map((project, idx) => (
                        <motion.div
                            key={idx}
                            className={`bento-item group relative h-full w-full [perspective:1000px] ${project.className} ${project.portfolioUrl ? 'cursor-pointer' : ''}`}
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                            onClick={() => project.portfolioUrl && navigateToProject(project.portfolioUrl)}
                            onKeyDown={(event) => {
                                if (!project.portfolioUrl) {
                                    return;
                                }

                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    navigateToProject(project.portfolioUrl);
                                }
                            }}
                            role={project.portfolioUrl ? 'link' : undefined}
                            tabIndex={project.portfolioUrl ? 0 : undefined}
                        >
                            <TiltCard className="h-full w-full">
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10 rounded-xl" />
                                <div className="absolute inset-0 bg-gray-900 animate-pulse group-hover:animate-none transition-colors duration-500 rounded-xl" />

                                <img src={project.image} alt={project.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-70 group-hover:opacity-100 rounded-xl" />

                                <div className="relative z-20 h-full flex flex-col justify-end p-6 rounded-xl">
                                    <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                        <span className="text-emerald-400 text-xs font-bold uppercase tracking-wider mb-2 block">
                                            {project.category}
                                        </span>
                                        <h3 className="text-2xl font-bold text-white mb-2">{project.title}</h3>
                                        <p className="text-gray-300 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                                            {project.description}
                                        </p>
                                        <div className="flex flex-wrap gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-200">
                                            {project.tags.map(tag => (
                                                <span key={tag} className="text-xs px-2 py-1 bg-white/10 rounded-full text-gray-200 border border-white/5">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                        {(project.portfolioUrl || project.liveUrl || project.contactUrl) && (
                                            <div className="mt-4 flex flex-wrap gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-300">
                                                {project.portfolioUrl && (
                                                    <a
                                                        href={project.portfolioUrl}
                                                        className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-emerald-200"
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        Case Study
                                                    </a>
                                                )}
                                                {project.liveUrl && (
                                                    <a
                                                        href={project.liveUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white"
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        Try It
                                                    </a>
                                                )}
                                                {project.contactUrl && (
                                                    <a
                                                        href={project.contactUrl}
                                                        className="rounded-full border border-fuchsia-400/30 bg-fuchsia-400/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-fuchsia-100"
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        Contact
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </TiltCard>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
